import React from 'react';
import {
    TrendingUp,
    AccountBalanceWallet,
    People,
    PointOfSale,
    ArrowUpward,
    ArrowDownward
} from '@mui/icons-material';

// Même format que mobile (K, M, B)
const formatNumber = (num) => {
    const n = num == null ? 0 : Number(num);
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2).replace(/\.0$/, '') + 'B';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(2).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(2).replace(/\.0$/, '') + 'K';
    return n.toFixed(2).toString();
};

const StatCard = ({ title, value, change, icon: Icon, trend, currency, isAmount = false, suffix, loading = false, formatAmountShort = false }) => {
    const formattedValue = isAmount
        ? formatAmountShort
            ? `${currency === 'USD' ? '$' : 'CDF '}${formatNumber(value && value[currency] != null ? value[currency] : 0)}`
            : `${currency === 'USD' ? '$' : ''}${(value && value[currency] != null ? value[currency] : 0).toLocaleString()}${currency === 'CDF' ? ' FC' : ''}`
        : (typeof value === 'number' ? value.toLocaleString() : (value || 0).toLocaleString()) + (suffix || '');

    // Couleurs ambre/doré basées sur la tendance
    const trendColors = {
        up: {
            bg: 'from-amber-500 to-orange-500',
            text: 'text-green-600',
            icon: 'text-amber-600',
            bgLight: 'bg-green-50',
            border: 'border-amber-200'
        },
        down: {
            bg: 'from-red-500 to-rose-500',
            text: 'text-red-600',
            icon: 'text-red-500',
            bgLight: 'bg-red-50',
            border: 'border-red-200'
        },
        neutral: {
            bg: 'from-amber-400 to-amber-500',
            text: 'text-amber-600',
            icon: 'text-amber-500',
            bgLight: 'bg-amber-50',
            border: 'border-amber-200'
        }
    };

    const colors = trendColors[trend] || trendColors.neutral;

    if (loading) {
        return (
            <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200/60 shadow-sm animate-pulse">
                <div className="flex items-center justify-between gap-2">
                    <div className="space-y-2 flex-1 min-w-0">
                        <div className="h-3 bg-gray-200 rounded w-2/3" />
                        <div className="h-5 bg-gray-200 rounded w-1/2" />
                    </div>
                    <div className="w-9 h-9 bg-gray-200 rounded-xl flex-shrink-0" />
                </div>
            </div>
        );
    }

    return (
        <div className={`
            group relative bg-white rounded-xl p-3 sm:p-4 border transition-all duration-300 hover:shadow-md
            shadow-sm hover:border-amber-300 overflow-hidden min-w-0
            ${colors.border} 
        `}>
            <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${colors.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

            <div className="relative z-10 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide truncate mb-0.5">
                        {title}
                    </p>
                    <p className="text-base sm:text-lg font-bold text-gray-900 truncate" title={formattedValue}>
                        {formattedValue}
                    </p>
                    {change && (
                        <div className={`flex items-center gap-1 mt-1 flex-wrap ${colors.text}`}>
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${colors.bgLight}`}>
                                {trend === 'up' ? <ArrowUpward className="w-2.5 h-2.5" /> : <ArrowDownward className="w-2.5 h-2.5" />}
                                {change}%
                            </span>
                            <span className="text-[10px] text-gray-500 hidden sm:inline">vs préc.</span>
                        </div>
                    )}
                </div>
                <div className={`p-1.5 sm:p-2 bg-gradient-to-br ${colors.bg} rounded-xl shadow flex-shrink-0`}>
                    <Icon className="text-white text-base sm:text-lg" />
                </div>
            </div>

            <div className="mt-2 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                <div
                    className={`h-1 rounded-full bg-gradient-to-r ${colors.bg} transition-all duration-1000 ease-out`}
                    style={{
                        width: `${Math.min(100, isAmount && value && value[currency] != null ? (Number(value[currency]) / 10000) * 100 : typeof value === 'number' ? value : 0)}%`
                    }}
                />
            </div>
        </div>
    );
};

const calcChange = (current, last) => {
    if (last == null || last === 0) return current > 0 ? 100 : 0;
    if (current == null) return 0;
    return Math.round(((Number(current) - Number(last)) / Number(last)) * 100);
};

const AdminStatsGrid = ({ stats, apiDashboard, currency, timeRange, loading = false }) => {
    const getTrendIcon = (value, previousValue) => {
        if (previousValue == null) return value > 0 ? 'up' : 'neutral';
        return value > previousValue ? 'up' : value < previousValue ? 'down' : 'neutral';
    };

    const hasApiDashboard = apiDashboard && (apiDashboard.dailyAmount != null || apiDashboard.monthlyAmount != null || apiDashboard.yearlyAmount != null);
    const dayChange = hasApiDashboard ? calcChange(apiDashboard.dailyAmount, apiDashboard.lastDayAmount) : null;
    const monthChange = hasApiDashboard ? calcChange(apiDashboard.monthlyAmount, apiDashboard.lastMonthAmount) : null;
    const yearChange = hasApiDashboard ? calcChange(apiDashboard.yearlyAmount, apiDashboard.lastYearAmount) : null;

    // Première ligne : 6 cartes principales (une seule rangée équilibrée)
    const statCards = [
        {
            title: hasApiDashboard ? "Aujourd'hui" : 'Chiffre d\'Affaires',
            value: hasApiDashboard ? { [currency]: apiDashboard.dailyAmount ?? 0 } : (stats?.totalAmount || { USD: 0, CDF: 0 }),
            change: dayChange != null ? (dayChange >= 0 ? `+${dayChange}` : `${dayChange}`) : undefined,
            trend: hasApiDashboard ? getTrendIcon(apiDashboard.dailyAmount, apiDashboard.lastDayAmount) : getTrendIcon(stats?.totalAmount?.[currency], stats?.previousTotalAmount?.[currency]),
            icon: AccountBalanceWallet,
            isAmount: true,
            formatAmountShort: !!hasApiDashboard
        },
        {
            title: hasApiDashboard ? 'Ce Mois' : 'Transactions',
            value: hasApiDashboard ? { [currency]: apiDashboard.monthlyAmount ?? 0 } : (stats?.totalTransactions ?? 0),
            change: monthChange != null ? (monthChange >= 0 ? `+${monthChange}` : `${monthChange}`) : undefined,
            trend: hasApiDashboard ? getTrendIcon(apiDashboard.monthlyAmount, apiDashboard.lastMonthAmount) : getTrendIcon(stats?.totalTransactions, stats?.previousTotalTransactions),
            icon: PointOfSale,
            isAmount: hasApiDashboard,
            formatAmountShort: !!hasApiDashboard
        },
        {
            title: hasApiDashboard ? 'Cette Année' : 'Taux de Réussite',
            value: hasApiDashboard ? { [currency]: apiDashboard.yearlyAmount ?? 0 } : (stats?.conversionRate ?? 0),
            change: yearChange != null ? (yearChange >= 0 ? `+${yearChange}` : `${yearChange}`) : undefined,
            trend: hasApiDashboard ? getTrendIcon(apiDashboard.yearlyAmount, apiDashboard.lastYearAmount) : getTrendIcon(stats?.conversionRate, stats?.previousConversionRate),
            icon: TrendingUp,
            suffix: !hasApiDashboard ? '%' : undefined,
            isAmount: hasApiDashboard,
            formatAmountShort: !!hasApiDashboard
        },
        {
            title: 'Transactions',
            value: stats?.totalTransactions ?? 0,
            change: undefined,
            trend: getTrendIcon(stats?.totalTransactions, stats?.previousTotalTransactions),
            icon: PointOfSale
        }
    ];

    // Taux de réussite et Clients actifs : affichage compact sous la grille (une ligne, style badge)
    const conversionRate = stats?.conversionRate ?? 0;
    const activeClients = stats?.activeClients ?? 0;

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {statCards.map((stat, index) => (
                    <StatCard
                        key={index}
                        title={stat.title}
                        value={stat.value}
                        change={stat.change}
                        trend={stat.trend}
                        icon={stat.icon}
                        currency={currency}
                        isAmount={stat.isAmount}
                        suffix={stat.suffix}
                        loading={loading}
                        formatAmountShort={stat.formatAmountShort}
                    />
                ))}
            </div>

            {/* Ligne compacte : Taux de réussite + Clients actifs */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 py-2.5 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-slate-50 to-amber-50/40 border border-slate-200/80">
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0">
                        <TrendingUp className="text-sm" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase">Taux de réussite</p>
                        <p className="text-sm sm:text-base font-bold text-slate-900 truncate">
                            {loading ? '—' : `${Number(conversionRate).toLocaleString()}%`}
                        </p>
                    </div>
                </div>
                <div className="hidden sm:block w-px h-8 bg-slate-200/80" aria-hidden />
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex-shrink-0">
                        <People className="text-sm" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase">Clients actifs</p>
                        <p className="text-sm sm:text-base font-bold text-slate-900 truncate">
                            {loading ? '—' : Number(activeClients).toLocaleString()}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminStatsGrid;